'use client';

import { motion } from 'framer-motion';
import { ArrowRight } from 'lucide-react';
import Image from 'next/image';
import { sectionHeadingTag } from '@/components/dynamic-zone/section-heading-level';
import { strapiImage } from '@/lib/strapi/strapiImage';
import { components } from '@/types/strapi';
import { SectionContainer } from '../../ui/SectionContainer';
// const IconComponent = ({
// 	name,
// 	className,
// }: {
// 	name?: string;
// 	className?: string;
// }) => {
// 	return <span className={className}>{name}</span>;
// };

export default function GridIconFeatures({
	heading,
	sub_heading,
	items,
	backgroundColor = 'bg-gradient-to-b from-white to-gray-50/50',
	CTAs,
	isFirst = false,
}: components['schemas']['DynamicZoneGridIconFeaturesComponent'] & {
	isFirst?: boolean;
}) {
	const SectionHeading = sectionHeadingTag(isFirst);
	return (
		<SectionContainer
			className={`grid-icon-features-section ${backgroundColor}`}
			data-testid="section-container">
			<noscript>
				<style>{`
					.grid-icon-features-section h2[style],
					.grid-icon-features-section h3[style],
					.grid-icon-features-section p[style],
					.grid-icon-features-section [style*='opacity'],
					.grid-icon-features-section [style*='translateY'],
					.grid-icon-features-section [style*='translateX'] {
						opacity: 1 !important;
						transform: none !important;
					}
				`}</style>
			</noscript>
			<div className="mb-12 text-center">
				<motion.div
					initial={{ opacity: 0, y: 20 }}
					whileInView={{ opacity: 1, y: 0 }}
					viewport={{ once: true }}
					transition={{ duration: 0.5 }}>
					<SectionHeading className="mb-4 bg-gradient-to-r from-primary via-blue-600 to-primary bg-clip-text text-4xl font-bold text-transparent md:text-5xl">
						{heading}
					</SectionHeading>
					{sub_heading && (
						<p className="mx-auto max-w-2xl text-lg leading-relaxed text-gray-600">
							{sub_heading}
						</p>
					)}
				</motion.div>
			</div>

			<div
				className="mx-auto grid max-w-6xl gap-6 md:grid-cols-2 lg:gap-8"
				data-testid="expertise-grid">
				{items?.map(
					(
						item: components['schemas']['ItemsIconTitleDescriptionIntermediateComponent'],
						index: number,
					) => (
						<motion.div
							key={index}
							data-testid="expertise-item"
							initial={{
								opacity: 0,
								x: index % 2 === 0 ? -20 : 20,
							}}
							whileInView={{ opacity: 1, x: 0 }}
							transition={{ duration: 0.5, delay: index * 0.1 }}
							viewport={{ once: true }}
							className="group relative overflow-hidden rounded-2xl border border-gray-100/50 bg-white/80 p-6 shadow-lg backdrop-blur-sm transition-all duration-300 hover:border-primary/20 hover:shadow-xl hover:shadow-primary/5">
							{/* Decorative elements */}
							<div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-transparent to-blue-500/5 opacity-0 transition-opacity duration-300 group-hover:opacity-100" />
							<div className="absolute right-0 top-0 h-24 w-24 -translate-y-8 translate-x-8 transform rounded-full bg-gradient-to-br from-primary/10 to-blue-500/10 blur-2xl filter group-hover:from-primary/20 group-hover:to-blue-500/20" />

							<div className="relative flex items-start gap-5">
								<div className="flex-shrink-0">
									<div
										data-testid="icon-container"
										className="flex h-16 w-16 transform items-center justify-center rounded-2xl bg-gradient-to-br from-primary/10 to-blue-500/10 transition-all duration-300 group-hover:scale-105 group-hover:from-primary/20 group-hover:to-blue-500/20">
										{/* <IconComponent name={item.icon} /> */}
										{item.icon?.url && (
											<Image
												src={strapiImage(item.icon.url)}
												alt={
													item.icon.alternativeText ||
													item.icon.name ||
													''
												}
												width={32}
												height={32}
												className="h-8 w-8 object-contain"
											/>
										)}
									</div>
								</div>
								<div className="flex-1">
									{item.title && (
										<h3 className="mb-2 text-xl font-bold text-gray-900 transition-colors duration-300 group-hover:text-primary lg:text-2xl">
											{item.title}
										</h3>
									)}

									{item.description && (
										<div
											className="text-base leading-relaxed text-gray-600 group-hover:text-gray-700 lg:text-lg"
											dangerouslySetInnerHTML={{
												__html: item.description,
											}}
										/>
									)}
								</div>
							</div>
						</motion.div>
					),
				)}
			</div>

			<motion.div
				initial={{ opacity: 0, y: 20 }}
				whileInView={{ opacity: 1, y: 0 }}
				transition={{ duration: 0.5, delay: 0.4 }}
				viewport={{ once: true }}
				className="mt-16 text-center">
				{CTAs?.length === 1 && (
					<a
						href={CTAs[0].URL}
						className="group inline-flex items-center gap-2 rounded-xl bg-gradient-to-r from-primary to-blue-600 px-8 py-4 text-lg font-semibold text-white transition-all duration-300 hover:gap-4 hover:shadow-lg hover:shadow-primary/20">
						{CTAs[0].text}
						<ArrowRight className="h-5 w-5 transition-transform group-hover:translate-x-1" />
					</a>
				)}
				{CTAs?.length === 2 && (
					<div className="flex justify-center gap-4">
						{CTAs.map((cta, index) => (
							<a
								key={index}
								href={cta.URL}
								className="group inline-flex items-center gap-2 rounded-xl bg-gradient-to-r from-primary to-blue-600 px-6 py-3 text-lg font-semibold text-white transition-all duration-300 hover:gap-4 hover:shadow-lg hover:shadow-primary/20">
								{cta.text}
								<ArrowRight className="h-5 w-5 transition-transform group-hover:translate-x-1" />
							</a>
						))}
					</div>
				)}
			</motion.div>
		</SectionContainer>
	);
}
